import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { courseV2Api } from '../services/api';
import { CourseV2 } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronRightIcon, ClockIcon, UsersIcon, CheckCircleIcon, AcademicCapIcon } from '../components/icons';

const CourseV2Page: React.FC = () => {
  const { colors } = useTheme();
  const [courses, setCourses] = useState<CourseV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExamType, setSelectedExamType] = useState<string>('all');

  const examTypes = ['all', 'IB', 'IGCSE', 'CBSE', 'AP', 'SAT', 'JEE', 'NEET'];

  useEffect(() => {
    fetchCourses();
  }, [selectedExamType]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      let response;
      if (selectedExamType === 'all') {
        response = await courseV2Api.getAll();
      } else {
        response = await courseV2Api.getByExamType(selectedExamType);
      }
      setCourses(response.data.data.courses);
      setError(null);
    } catch (err) {
      setError('Failed to load courses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const CourseCard: React.FC<{ course: CourseV2 }> = ({ course }) => {
    return (
      <Link
        to={`/coursesv2/${course._id}`}
        className="group block rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        style={{
          backgroundColor: colors.background.paper,
          boxShadow: `0 4px 20px ${colors.primary.main}10`
        }}
      >
        <div className="relative h-56">
          <img 
            src={course.image || `https://picsum.photos/seed/${course._id}/400/300`} 
            alt={course.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = `https://picsum.photos/seed/${course._id}/400/300`;
            }}
          />
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main}80 0%, ${colors.accent.mint}80 100%)`
            }}
          />
          <div className="absolute top-4 left-4">
            <span 
              className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{
                backgroundColor: colors.accent.warmYellow,
                color: colors.primary.contrastText
              }}
            >
              {course.examType}
            </span>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-2xl font-bold text-white mb-2">
              {course.title}
            </h3>
            <div className="flex flex-wrap gap-2">
              {course.subjects.slice(0, 3).map((subject, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm"
                >
                  {subject}
                </span>
              ))}
              {course.subjects.length > 3 && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                  +{course.subjects.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <p className="mb-4 line-clamp-2" style={{ color: colors.text.secondary }}>
            {course.description}
          </p>
          
          <div className="flex items-center gap-4 mb-4 text-sm">
            <div className="flex items-center gap-1">
              <UsersIcon className="w-4 h-4" style={{ color: colors.accent.mint }} />
              <span style={{ color: colors.text.secondary }}>{course.mode}</span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" style={{ color: colors.accent.mint }} />
              <span style={{ color: colors.text.secondary }}>{course.duration}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            {course.price?.hourly ? (
              <div>
                <p className="text-sm" style={{ color: colors.text.secondary }}>Starting from</p>
                <p className="text-2xl font-bold" style={{ color: colors.primary.main }}>
                  ${course.price.hourly}/hr
                </p>
              </div>
            ) : (
              <div />
            )}
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-300 group-hover:gap-3"
              style={{
                borderColor: colors.accent.mint,
                color: colors.accent.mint
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Know more
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen pt-16" style={{ backgroundColor: colors.background.default }}>
      {/* Hero Section */}
      <section 
        className="relative py-20 overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)` 
        }}
      >
        <div className="absolute inset-0">
          <div 
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.accent.mint}20 0%, transparent 70%)`,
              animationDelay: '0s'
            }}
          />
          <div 
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.primary.main}20 0%, transparent 70%)`,
              animationDelay: '2s'
            }}
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: colors.text.primary }}>
            One-on-One Live Tutoring for
            <span 
              className="block mt-2"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Academic Excellence
            </span>
          </h1>
          <p className="text-lg max-w-3xl mx-auto mb-8" style={{ color: colors.text.secondary }}>
            Personalized coaching for IB, IGCSE, AP, SAT, JEE, NEET and more. 
            Learn from expert instructors in one-on-one sessions tailored to your needs.
          </p>
          
          {/* Exam Type Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {examTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedExamType(type)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 ${
                  selectedExamType === type ? 'shadow-lg' : ''
                }`}
                style={{
                  background: selectedExamType === type 
                    ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                    : colors.background.paper,
                  color: selectedExamType === type 
                    ? colors.primary.contrastText 
                    : colors.text.primary,
                  border: selectedExamType === type
                    ? 'none'
                    : `1px solid ${colors.border.main}`,
                  boxShadow: selectedExamType === type
                    ? `0 4px 20px ${colors.primary.main}40`
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (selectedExamType !== type) {
                    e.currentTarget.style.backgroundColor = `${colors.primary.main}10`;
                    e.currentTarget.style.borderColor = colors.primary.main;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedExamType !== type) {
                    e.currentTarget.style.background = colors.background.paper;
                    e.currentTarget.style.borderColor = colors.border.main;
                  }
                }}
              >
                {type === 'all' ? 'All Courses' : type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-lg mb-4" style={{ color: colors.error.main }}>{error}</p>
              <button
                onClick={fetchCourses}
                className="px-6 py-3 rounded-lg font-medium"
                style={{
                  backgroundColor: colors.primary.main,
                  color: colors.primary.contrastText
                }}
              >
                Try Again
              </button>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8">
              <AcademicCapIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text.secondary }} />
              <p className="text-lg" style={{ color: colors.text.secondary }}>
                No courses available for {selectedExamType === 'all' ? 'this category' : selectedExamType} yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course) => (
                <CourseCard key={course._id} course={course} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section 
        className="py-16"
        style={{ backgroundColor: colors.background.paper }}
      >
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: colors.text.primary }}>
            Why Choose Our One-on-One Tutoring?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Personalized Learning",
                description: "Customized study plans tailored to your specific needs and learning pace",
                icon: UsersIcon
              },
              {
                title: "Expert Instructors",
                description: "Learn from qualified teachers with years of experience in competitive exam preparation",
                icon: AcademicCapIcon
              },
              {
                title: "Flexible Scheduling",
                description: "Choose class timings that work best for you with our flexible scheduling system",
                icon: ClockIcon
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl"
                style={{
                  backgroundColor: colors.background.default,
                  boxShadow: `0 4px 20px ${colors.primary.main}10`
                }}
              >
                <div 
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                  }}
                >
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text.primary }}>
                  {feature.title}
                </h3>
                <p style={{ color: colors.text.secondary }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseV2Page;